// src/context/GameContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BACKEND } from "../utils/api";

const GameContext = createContext(null);

const getToken = () => {
  return localStorage.getItem("token") || localStorage.getItem("mintza_token");
};

export function GameProvider({ children }) {
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${BACKEND}/api/wallet/balance`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });

      if (res.ok) {
        const data = await res.json();
        setBalance(data.balance ?? data.data?.balance ?? 0);
      }
    } catch (err) {
      console.error("Failed to fetch balance:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const updateBalance = useCallback((newBalance) => {
    setBalance(newBalance);
    // Also update on backend
    const token = getToken();
    if (token) {
      fetch(`${BACKEND}/api/wallet/sync`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ balance: newBalance }),
      }).catch(() => {});
    }
  }, []);

  const addBalance = useCallback((amount) => {
    setBalance((prev) => {
      const newBal = prev + amount;
      // Update backend in background
      const token = getToken();
      if (token) {
        fetch(`${BACKEND}/api/wallet/sync`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ balance: newBal }),
        }).catch(() => {});
      }
      return newBal;
    });
  }, []);

  return (
    <GameContext.Provider value={{ balance, setBalance, updateBalance, addBalance, loading, refetch: fetchBalance }}>
      {children}
    </GameContext.Provider>
  );
}

export function useGameBalance() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error("useGameBalance must be used within a GameProvider");
  }
  return context;
}
